import React from 'react';

import styled from 'styled-components';

import { useCommunityViewContext } from 'components/View/CommunityViewContext';
import {
  convertAreaByUnitSystem,
  convertRatePerAreaByUnitSystem,
  getAreaUnitLabel,
  getRatePerAreaUnitLabel,
} from 'utils/helpers/units';

import CMASummaryTableDataValuesDifference from './CMASummaryTableDataValuesDifference';
import {
  getFeeDiffTestId,
  getFeePerSqftDiffTestId,
  getFeePerSqftTestId,
  getSqftDiffTestId,
  getSqftTestId,
} from './testIdUtils';
import { CMADataCellValue, CMASummaryFilters } from './types';
import { isDefined, parseCMASummaryCommunityData } from './utils';

const Container = styled.div`
  display: flex;
  flex-flow: column;
`;

const Title = styled.div`
  font-family: var(--font-regular);
  font-size: 14px;
  white-space: nowrap;
`;

const Subtitle = styled.span<{ highlight?: boolean }>`
  font-size: 12px;
  color: ${(props) => (props.highlight ? 'var(--inactive-orange)' : 'var(--text-secondary)')};
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
`;

type Props = {
  livingInfo?: CMADataCellValue[];
  dataTestId?: string;
};

const CMASummaryTableDataLivingInfo = ({ livingInfo, dataTestId }: Props) => {
  const { viewFilters, community } = useCommunityViewContext();
  const filters = viewFilters as CMASummaryFilters;
  const areaUnitLabel = getAreaUnitLabel(community);
  const ratePerAreaUnitLabel = getRatePerAreaUnitLabel(community);

  const { dataItemOne, dataItemTwo, dataItemThree, dataItemFour } = parseCMASummaryCommunityData(
    livingInfo,
    filters?.viewOptions?.valueDisplay
  );

  let entranceFeeContainer, squareFootageContainer, pricePerSquareFootageContainer;

  const rentValue = dataItemOne?.value;
  const rentDifference = dataItemOne?.difference;
  const entranceFeeValue = dataItemTwo?.value;
  const entranceFeeDifference = dataItemTwo?.difference;
  const squareFootageValue = dataItemThree?.value;
  const squareFootageDifference = dataItemThree?.difference;
  const squareFootageHighlight = dataItemThree?.highlight;
  const pricePerSqFt = dataItemFour?.value;
  const pricePerSqFtDifference = dataItemFour?.difference;

  if (isDefined(entranceFeeValue)) {
    entranceFeeContainer = (
      <Subtitle>
        {entranceFeeValue.toLocaleString()} ent.fee
        <CMASummaryTableDataValuesDifference difference={entranceFeeDifference} />
      </Subtitle>
    );
  }
  if (isDefined(squareFootageValue)) {
    const displayedAreaValue = convertAreaByUnitSystem(squareFootageValue, community);
    const displayedAreaDiff =
      typeof squareFootageDifference === 'number'
        ? convertAreaByUnitSystem(squareFootageDifference, community)
        : squareFootageDifference;
    squareFootageContainer = (
      <Subtitle highlight={squareFootageHighlight} data-test-id={getSqftTestId(dataTestId)}>
        {Math.round(displayedAreaValue).toLocaleString()} {areaUnitLabel}
        <CMASummaryTableDataValuesDifference
          difference={displayedAreaDiff}
          prefix=""
          dataTestId={getSqftDiffTestId(dataTestId)}
        />
      </Subtitle>
    );
  }
  if (pricePerSqFt !== undefined && pricePerSqFt !== null) {
    const displayedRatePerArea = convertRatePerAreaByUnitSystem(pricePerSqFt, community);
    const displayedRatePerAreaDiff =
      typeof pricePerSqFtDifference === 'number'
        ? convertRatePerAreaByUnitSystem(pricePerSqFtDifference, community)
        : pricePerSqFtDifference;
    pricePerSquareFootageContainer = (
      <Subtitle data-test-id={getFeePerSqftTestId(dataTestId)}>
        ${Math.round(displayedRatePerArea).toLocaleString()} {ratePerAreaUnitLabel}
        <CMASummaryTableDataValuesDifference
          difference={displayedRatePerAreaDiff}
          prefix=""
          dataTestId={getFeePerSqftDiffTestId(dataTestId)}
        />
      </Subtitle>
    );
  }

  return (
    <div data-test-id={dataTestId}>
      {isDefined(rentValue) ? (
        <Container>
          <Title>
            ${rentValue.toLocaleString()}
            <CMASummaryTableDataValuesDifference
              difference={rentDifference}
              dataTestId={getFeeDiffTestId(dataTestId)}
            />
          </Title>
          {entranceFeeContainer}
          {squareFootageContainer}
          {pricePerSquareFootageContainer}
        </Container>
      ) : (
        <div>-</div>
      )}
    </div>
  );
};

export default CMASummaryTableDataLivingInfo;
